/**
 * Policy XACML Viewer Component
 * Displays XACML JSON content in a formatted code block with syntax highlighting
 */

import { useState, useEffect, useRef } from 'react'
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import Prism from 'prismjs'
import 'prismjs/components/prism-json'

interface PolicyXACMLViewerProps {
  /** XACML policy content as JSON object */
  xacmlContent: Record<string, unknown> | null
  /** Optional class name for styling */
  className?: string
  /** Initial collapsed state */
  defaultCollapsed?: boolean
  /** Maximum height before scrolling (default: 400px) */
  maxHeight?: string
}

/**
 * PolicyXACMLViewer Component
 * Displays XACML JSON with syntax highlighting, copy functionality, and collapsible sections
 */
export function PolicyXACMLViewer({
  xacmlContent,
  className,
  defaultCollapsed = false,
  maxHeight = '400px',
}: PolicyXACMLViewerProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
  const [copied, setCopied] = useState(false)
  const codeRef = useRef<HTMLElement>(null)

  // Format JSON with 2-space indentation
  const jsonString = xacmlContent ? JSON.stringify(xacmlContent, null, 2) : ''

  // Apply Prism.js syntax highlighting when content changes or collapse state changes
  useEffect(() => {
    if (codeRef.current && xacmlContent && !isCollapsed) {
      Prism.highlightElement(codeRef.current)
    }
  }, [xacmlContent, isCollapsed])

  // Handle null or undefined content
  if (!xacmlContent) {
    return (
      <div
        className={cn(
          'rounded-lg border border-border bg-muted p-4 text-sm text-muted-foreground',
          className
        )}
      >
        No XACML content available
      </div>
    )
  }

  // Copy to clipboard handler
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-muted overflow-hidden',
        className
      )}
    >
      {/* Header with controls */}
      <div className="flex items-center justify-between border-b border-border bg-muted/80 px-4 py-2">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand XACML content' : 'Collapse XACML content'}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          XACML Policy Content
        </button>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 px-2 text-muted-foreground hover:text-foreground hover:bg-accent"
          aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
        >
          {copied ? (
            <>
              <Check className="mr-1.5 h-3.5 w-3.5 text-green-500" />
              <span className="text-xs">Copied</span>
            </>
          ) : (
            <>
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              <span className="text-xs">Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Code content */}
      {!isCollapsed && (
        <div
          className="overflow-auto p-4 bg-card"
          style={{ maxHeight }}
        >
          <pre className="font-mono text-sm leading-relaxed">
            <code ref={codeRef} className="language-json">
              {jsonString}
            </code>
          </pre>
        </div>
      )}
    </div>
  )
}

export default PolicyXACMLViewer
